import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import useSeo, { CANONICAL_BASE } from "@/lib/useSeo";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function FilterGroup({ title, options, active, onToggle }) {
  if (!options?.length) return null;
  return (
    <div className="border-b border-[#E8E1D8] py-5">
      <p className="label-x mb-3">{title}</p>
      <div className="flex flex-wrap gap-2">
        {options.filter(Boolean).map((o) => (
          <button key={o} onClick={() => onToggle(o)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wide border transition-colors ${active === o ? "bg-[#1D1D1B] text-[#F7F5F1] border-[#1D1D1B]" : "border-[#E8E1D8] hover:border-[#1D1D1B]"}`}
            data-testid={`filter-${title.toLowerCase()}-${o}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [facets, setFacets] = useState({});
  const [data, setData] = useState({ products: [], total: 0 });
  const [loading, setLoading] = useState(true);
  useSeo({ title: "Shop — ONUSA", description: "Shop de ONUSA PROTOCOL collectie. Premium contemporary streetwear.", canonical: CANONICAL_BASE + "/shop" });

  useEffect(() => { api.get("/products/facets").then(({ data }) => setFacets(data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/products?${params.toString()}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || next.get(k) === v) next.delete(k);
    else next.set(k, v);
    setParams(next);
  };
  const clearAll = () => setParams(new URLSearchParams());
  const activeCount = ["category", "collection", "color", "size", "availability"].filter((k) => params.get(k)).length;

  const Filters = () => (
    <div>
      <FilterGroup title="Categorie" options={facets.categories} active={params.get("category")} onToggle={(v) => setParam("category", v)} />
      <FilterGroup title="Collectie" options={facets.collections} active={params.get("collection")} onToggle={(v) => setParam("collection", v)} />
      <FilterGroup title="Kleur" options={facets.colors} active={params.get("color")} onToggle={(v) => setParam("color", v)} />
      <FilterGroup title="Maat" options={facets.sizes} active={params.get("size")} onToggle={(v) => setParam("size", v)} />
      <FilterGroup title="Beschikbaarheid" options={["in_stock", "sale"]} active={params.get("availability")} onToggle={(v) => setParam("availability", v)} />
      {activeCount > 0 && <button onClick={clearAll} className="label-x mt-5 flex items-center gap-1" data-testid="clear-filters"><X size={13} /> WIS ALLES</button>}
    </div>
  );

  return (
    <div className="onusa-container py-10 md:py-16" data-testid="shop-page">
      <div className="mb-8">
        <p className="label-x">DE WINKEL</p>
        <h1 className="heading-x text-3xl md:text-5xl mt-2">SHOP ALLES</h1>
      </div>
      <div className="flex items-center justify-between mb-6 border-y border-[#E8E1D8] py-3">
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button className="flex items-center gap-2 label-x" data-testid="mobile-filter-trigger"><SlidersHorizontal size={15} /> FILTER {activeCount ? `(${activeCount})` : ""}</button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#F7F5F1] overflow-y-auto"><SheetTitle className="sr-only">Filters</SheetTitle><div className="mt-6"><Filters /></div></SheetContent>
          </Sheet>
        </div>
        <p className="label-x hidden md:block">{data.total} STUKS</p>
        <Select value={params.get("sort") || "featured"} onValueChange={(v) => setParam("sort", v === "featured" ? "" : v)}>
          <SelectTrigger className="w-44 border-0 label-x focus:ring-0" data-testid="sort-select"><SelectValue placeholder="SORTEREN" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="featured">UITGELICHT</SelectItem>
            <SelectItem value="newest">NIEUWSTE</SelectItem>
            <SelectItem value="price_asc">PRIJS: LAAG → HOOG</SelectItem>
            <SelectItem value="price_desc">PRIJS: HOOG → LAAG</SelectItem>
            <SelectItem value="name">NAAM</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden md:block"><Filters /></aside>
        <div>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">{[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] bg-[#E8E1D8] animate-pulse" />)}</div>
          ) : data.products.length === 0 ? (
            <p className="label-x py-20 text-center">GEEN PRODUCTEN GEVONDEN</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {data.products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
